import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { 
  getNotificationsForUserType, 
  markNotificationAsRead, 
  getUserNotificationStatus 
} from '../services/notificationService.js';

export const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [userNotificationStatus, setUserNotificationStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications based on user role
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!currentUser) {
        setNotifications([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Map the role to the correct target group
        let userType;
        if (currentUser.role === 'admin') {
          userType = 'admin';
        } else if (currentUser.role === 'partner') {
          userType = 'partners'; // Match the targetGroups in notifications
        } else {
          userType = 'customers'; // Match the targetGroups in notifications
        }
        
        console.log('Fetching notifications for user type:', userType);
        const fetchedNotifications = await getNotificationsForUserType(userType);
        console.log('Fetched notifications:', fetchedNotifications);
        
        // Get user's notification read status
        const status = await getUserNotificationStatus(currentUser.uid);
        setUserNotificationStatus(status);
        
        setNotifications(fetchedNotifications);
        
        // Calculate unread count
        const unreadNotifications = fetchedNotifications.filter(
          notification => !status[notification.id] || !status[notification.id].isRead
        );
        setUnreadCount(unreadNotifications.length);
        
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
    
    // Setup a timer to refresh notifications every 5 minutes
    const refreshInterval = setInterval(fetchNotifications, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [currentUser]);

  // Mark a notification as read
  const markAsRead = async (notificationId) => {
    if (!currentUser) return;
    
    try {
      await markNotificationAsRead(currentUser.uid, notificationId);
      
      // Update local state
      setUserNotificationStatus(prev => ({
        ...prev,
        [notificationId]: { isRead: true, readAt: new Date() }
      }));
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to update notification status');
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!currentUser || notifications.length === 0) return;
    
    try {
      const promises = notifications.map(notification => 
        markNotificationAsRead(currentUser.uid, notification.id)
      );
      
      await Promise.all(promises);
      
      // Update local state
      const newStatus = {};
      notifications.forEach(notification => {
        newStatus[notification.id] = { isRead: true, readAt: new Date() };
      });
      
      setUserNotificationStatus(prev => ({
        ...prev,
        ...newStatus
      }));
      
      // Update unread count
      setUnreadCount(0);
      
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to update notification status');
    }
  };

  // Check if a notification is read
  const isNotificationRead = (notificationId) => {
    return userNotificationStatus[notificationId]?.isRead || false;
  };

  const value = {
    notifications,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isNotificationRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 